import React from "react";
import useFeatureFlags from "../hooks/useFeatureFlags";

const ProgressiveAchievement = ({ booksCount, versesCount, books }) => {
  const featureFlags = useFeatureFlags();

  // Don't render if the feature is disabled
  if (!featureFlags.biblicalExplorationProgress) {
    return null;
  }

  // Calculate progress milestones
  const bookProgress = (booksCount / 66) * 100;
  let achievement;

  if (booksCount === 0) {
    achievement = {
      emoji: "🌟",
      title: "Begin Your Journey!",
      message:
        "Start exploring the Bible to discover amazing stories and wisdom",
      bgColor: "from-gray-50 to-blue-50",
      borderColor: "border-gray-200",
      titleColor: "text-gray-700",
      messageColor: "text-gray-600",
      level: "Beginner",
    };
  } else if (booksCount === 1) {
    achievement = {
      emoji: "🎊",
      title: "First Steps Taken!",
      message: `Amazing! You've started your biblical journey with ${books[0]}. Every great adventure begins with a single step!`,
      bgColor: "from-green-50 to-emerald-50",
      borderColor: "border-green-200",
      titleColor: "text-green-800",
      messageColor: "text-green-600",
      level: "Newcomer",
    };
  } else if (booksCount <= 5) {
    achievement = {
      emoji: "🚀",
      title: "Building Momentum!",
      message: `You're making great progress! ${booksCount} books explored and ${versesCount} verses discovered. Keep going!`,
      bgColor: "from-blue-50 to-cyan-50",
      borderColor: "border-blue-200",
      titleColor: "text-blue-800",
      messageColor: "text-blue-600",
      level: "Explorer",
    };
  } else if (booksCount <= 15) {
    achievement = {
      emoji: "⭐",
      title: "Steady Explorer!",
      message: `Impressive dedication! You've explored ${booksCount} books (${Math.round(
        bookProgress
      )}% of the Bible) and found ${versesCount} verses. You're becoming a biblical scholar!`,
      bgColor: "from-amber-50 to-yellow-50",
      borderColor: "border-amber-200",
      titleColor: "text-amber-800",
      messageColor: "text-amber-600",
      level: "Scholar",
    };
  } else if (booksCount <= 33) {
    achievement = {
      emoji: "🏆",
      title: "Dedicated Scholar!",
      message: `Outstanding journey! You've covered ${booksCount} books (${Math.round(
        bookProgress
      )}% of the Bible) across ${versesCount} verses. You're well on your way to biblical mastery!`,
      bgColor: "from-purple-50 to-violet-50",
      borderColor: "border-purple-200",
      titleColor: "text-purple-800",
      messageColor: "text-purple-600",
      level: "Advanced Scholar",
    };
  } else if (booksCount <= 50) {
    achievement = {
      emoji: "🎖️",
      title: "Biblical Expert!",
      message: `Incredible achievement! You've explored ${booksCount} books (${Math.round(
        bookProgress
      )}% complete) and discovered ${versesCount} verses. You're approaching biblical expertise!`,
      bgColor: "from-indigo-50 to-blue-50",
      borderColor: "border-indigo-200",
      titleColor: "text-indigo-800",
      messageColor: "text-indigo-600",
      level: "Expert",
    };
  } else if (booksCount < 66) {
    achievement = {
      emoji: "👑",
      title: "Biblical Master!",
      message: `Phenomenal dedication! You've mastered ${booksCount} books (${Math.round(
        bookProgress
      )}% of the Bible) and ${versesCount} verses. You're so close to completing your biblical journey!`,
      bgColor: "from-rose-50 to-pink-50",
      borderColor: "border-rose-200",
      titleColor: "text-rose-800",
      messageColor: "text-rose-600",
      level: "Master",
    };
  } else {
    achievement = {
      emoji: "🎯",
      title: "Complete Biblical Scholar!",
      message: `INCREDIBLE! You've explored ALL 66 books of the Bible and discovered ${versesCount} verses. You've completed an amazing spiritual and educational journey!`,
      bgColor:
        "from-gradient-to-r from-yellow-50 via-orange-50 to-red-50",
      borderColor: "border-yellow-300",
      titleColor: "text-yellow-800",
      messageColor: "text-yellow-700",
      level: "Grandmaster",
    };
  }

  return (
    <div
      className={`bg-gradient-to-br ${achievement.bgColor} rounded-xl p-6 border-2 border-dashed ${achievement.borderColor} mb-8 shadow-lg relative overflow-hidden`}>
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-24 h-24 opacity-5 transform rotate-12 translate-x-6 -translate-y-6">
        <div className="text-6xl">{achievement.emoji}</div>
      </div>

      <div className="relative z-10">
        {/* Level Badge */}
        <div className="flex items-center justify-between mb-4">
          <div
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${achievement.titleColor} bg-white shadow-sm border ${achievement.borderColor}`}>
            {achievement.level}
          </div>
          <div className="text-2xl">{achievement.emoji}</div>
        </div>

        {/* Main Achievement */}
        <div className="text-center">
          <h3
            className={`text-2xl font-bold ${achievement.titleColor} mb-3 leading-tight`}>
            {achievement.title}
          </h3>
          <p
            className={`text-base ${achievement.messageColor} leading-relaxed`}>
            {achievement.message}
          </p>

          {/* Progress Stats */}
          {booksCount > 0 && (
            <div className="mt-4 pt-4 border-t border-white/50">
              <div className="flex justify-center items-center gap-6 text-sm font-medium">
                <div
                  className={`flex items-center gap-2 ${achievement.titleColor}`}>
                  <span className="text-lg">📚</span>
                  <span>{booksCount} Books</span>
                </div>
                <div
                  className={`flex items-center gap-2 ${achievement.titleColor}`}>
                  <span className="text-lg">📖</span>
                  <span>{versesCount} Verses</span>
                </div>
                <div
                  className={`flex items-center gap-2 ${achievement.titleColor}`}>
                  <span className="text-lg">📊</span>
                  <span>
                    {Math.round(bookProgress)}% Complete
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProgressiveAchievement;